import React, { createContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuth from '../hooks/useAuth';
import {
  getAllResignations,
  getResignationById,
  createResignation,
  updateResignation,
  deleteResignation
} from '../services/resignationService';
import Notification from '../components/Common/Notification';

export const ResignationContext = createContext();

export const ResignationProvider = ({ children }) => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [resignations, setResignations] = useState([]);
  const [selectedResignation, setSelectedResignation] = useState(null);
  const [notification, setNotification] = useState({ message: '', isError: false });

  const fetchAuthHeaders = () => {
    const storedUser = localStorage.getItem('user');
    if (!user && !storedUser) {
      console.error('User or token not available');
      return {};
    }

    const token = user?.token || JSON.parse(storedUser).token;
    return { Authorization: `Bearer ${token}` };
  };

  const showNotification = (message, isError) => {
    setNotification({ message, isError });
  };

  const closeNotification = () => {
    setNotification({ message: '', isError: false });
  };

  const fetchAllResignations = async () => {
    try {
      const authHeaders = fetchAuthHeaders();
      const data = await getAllResignations(authHeaders);
      setResignations(data);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error fetching resignations.';
      showNotification(errorMessage, true);
    }
  };

  const fetchResignationById = async (id) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const data = await getResignationById(id, authHeaders);
      setSelectedResignation(data);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || `Error fetching resignation with ID ${id}.`;
      showNotification(errorMessage, true);
    }
  };

  const addResignation = async (resignationData) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const newResignation = await createResignation(resignationData, authHeaders);
      setResignations(prevResignations => [...prevResignations, newResignation]);
      showNotification('Resignation added successfully!', false);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error adding resignation.';
      showNotification(errorMessage, true);
    }
  };

  const editResignation = async (id, resignationData) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const updatedResignation = await updateResignation(id, resignationData, authHeaders);
      setResignations(prevResignations =>
        prevResignations.map(resignation => (resignation.id === id ? updatedResignation : resignation))
      );
      showNotification('Resignation updated successfully!', false);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error updating resignation.';
      showNotification(errorMessage, true);
    }
  };

  const removeResignation = async (id) => {
    try {
      const authHeaders = fetchAuthHeaders();
      await deleteResignation(id, authHeaders);
      setResignations(prevResignations => prevResignations.filter(resignation => resignation.id !== id));
      showNotification('Resignation removed successfully!', false);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error removing resignation.';
      showNotification(errorMessage, true);
    }
  };

  // useEffect(() => {
  //   fetchAllResignations();
  // }, []);


  useEffect(() => {
    if (!isAuthenticated && !['/', '/login'].includes(window.location.pathname)) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);


  return (
    <ResignationContext.Provider
      value={{
        resignations,
        selectedResignation,
        fetchAllResignations,
        fetchResignationById,
        addResignation,
        editResignation,
        removeResignation,
      }}
    >
      {children}
      {notification.message && (
        <Notification
          message={notification.message}
          isError={notification.isError}
          onClose={closeNotification}
        />
      )}
    </ResignationContext.Provider>
  );
};
